import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { classifyLogLine } from '../../utilities/locatorDrift/locatorFormatters';
import {
  fetchLocatorRun,
  fetchLocatorRunLogs,
  runLocatorScenario,
} from '../../services/locatorDriftService';
import { setActiveTab } from './locatorCoreSlice';
import { errorMessage } from './locatorDriftUtils';

// Start unused Locator Drift scenario runner Redux.
// Retained for the non-batch runner flow, although the current LocatorDriftPage UI does not render it.
export const runLocatorScenarioAsync = createAsyncThunk(
  'locatorDrift/runs/runScenario',
  async ({ flowId, scenarioId, platform }, { dispatch, rejectWithValue }) => {
    dispatch(setActiveTab('results'));

    try {
      const data = await runLocatorScenario(flowId, scenarioId, platform);
      return {
        ...data,
        flowId,
        scenarioId,
        platform,
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const pollLocatorRunAsync = createAsyncThunk(
  'locatorDrift/runs/pollRun',
  async (_, { getState, rejectWithValue }) => {
    const { activeRunId, logIndex } = getState().locatorDrift.runs;
    if (!activeRunId) {
      return { skipped: true };
    }

    try {
      const [status, logData] = await Promise.all([
        fetchLocatorRun(activeRunId),
        fetchLocatorRunLogs(activeRunId, logIndex),
      ]);

      return {
        status,
        logs: Array.isArray(logData?.logs) ? logData.logs : [],
        nextFrom: logData?.nextFrom ?? logData?.next_from ?? logIndex,
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

const locatorRunsSlice = createSlice({
  name: 'locatorDriftRuns',
  initialState: {
    activeRunId: null,
    runStatus: null,
    runInfo: '',
    runLogs: [],
    logIndex: 0,
    runStarting: false,
    runError: null,
  },
  reducers: {
    resetRun(state) {
      state.activeRunId = null;
      state.runStatus = null;
      state.runInfo = '';
      state.runLogs = [];
      state.logIndex = 0;
      state.runError = null;
    },
    appendRunLog(state, action) {
      const text = action.payload?.text || '';
      state.runLogs.push({
        text,
        className: action.payload?.className || classifyLogLine(text),
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(runLocatorScenarioAsync.pending, (state, action) => {
        state.runStarting = true;
        state.runError = null;
        state.activeRunId = null;
        state.logIndex = 0;
        state.runLogs = [];
        state.runStatus = 'queued';
        const { flowId, scenarioId } = action.meta.arg;
        state.runInfo = `${flowId} / ${scenarioId}`;
      })
      .addCase(runLocatorScenarioAsync.fulfilled, (state, action) => {
        state.runStarting = false;
        state.activeRunId = action.payload?.runId || action.payload?.run_id;
        state.runStatus = 'queued';
        if (action.payload?.command) {
          state.runLogs.push({
            text: `Run started: ${state.activeRunId}`,
            className: 'info',
          });
          state.runLogs.push({
            text: `Command: ${action.payload.command}`,
            className: 'info',
          });
        }
      })
      .addCase(runLocatorScenarioAsync.rejected, (state, action) => {
        state.runStarting = false;
        state.runStatus = 'failed';
        state.runError = action.payload;
        state.runLogs.push({
          text: `Failed to start run: ${action.payload}`,
          className: 'fail',
        });
      })
      .addCase(pollLocatorRunAsync.fulfilled, (state, action) => {
        if (action.payload?.skipped) return;

        const status = action.payload.status || {};
        const rawStatus = status.status || state.runStatus;
        state.runStatus =
          rawStatus === 'finished' && status.exitCode !== undefined && status.exitCode !== 0
            ? 'failed'
            : rawStatus;

        action.payload.logs.forEach((line) => {
          state.runLogs.push({
            text: line,
            className: classifyLogLine(line),
          });
        });
        state.logIndex = action.payload.nextFrom;

        if (rawStatus === 'finished') {
          state.runLogs.push({
            text: `Run finished (exit code: ${status.exitCode ?? 'unknown'})`,
            className: status.exitCode === 0 ? 'success' : 'fail',
          });
        }
      })
      .addCase(pollLocatorRunAsync.rejected, (state, action) => {
        state.runLogs.push({
          text: `Poll error: ${action.payload}`,
          className: 'fail',
        });
      });
  },
});

export const { appendRunLog, resetRun } = locatorRunsSlice.actions;
export default locatorRunsSlice.reducer;
// End unused Locator Drift scenario runner Redux.
